import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import { FileAttachment } from '~hooks/useFileAttachment/models';
import { ATTACHMENT_IMAGE_CROSS_ORIGIN } from '~utils/image/attachmentImageCrossOrigin';
import { fileIsImage } from '~utils/utilFunctions';

const DEFAULT_IMAGE_SIZE_IN_PX = 50;

interface FileColumnProps {
  file: FileAttachment;
  imageSizeInPx?: number;
  sx?: SxProps<Theme>;
}

export default function FileColumn({
  file,
  imageSizeInPx = DEFAULT_IMAGE_SIZE_IN_PX,
  sx,
}: FileColumnProps) {
  const { fileName, fileUrl, mimeType, previewUrl } = file;

  const isImage = fileIsImage(mimeType);

  return (
    <Box sx={{ ...sx, overflow: 'hidden' }}>
      <Box sx={{ width: '100%', display: 'flex', gap: 2 }}>
        {isImage ? (
          <AttachmentImage sizeInPx={imageSizeInPx} url={previewUrl} />
        ) : (
          <AttachmentPdf sizeInPx={imageSizeInPx} />
        )}

        <PseudoLink
          title={fileName}
          action={() => window.open(fileUrl, '_blank', 'noopener,noreferrer')}
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        />
      </Box>
    </Box>
  );
}

interface AttachmentImageProps {
  sizeInPx: number;
  url: string;
}

function AttachmentImage({ sizeInPx, url }: AttachmentImageProps) {
  return (
    <Box
      data-test-id="file-thumbnail"
      sx={(theme) => ({
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 1,
        display: 'flex',
        flex: 0,
        height: `${sizeInPx}px`,
        minWidth: `${sizeInPx}px`,
        overflow: 'hidden',
      })}
    >
      <Box
        component="img"
        alt="ticket-preview"
        crossOrigin={ATTACHMENT_IMAGE_CROSS_ORIGIN}
        src={url}
        sx={{
          display: 'block',
          objectFit: 'cover',
          objectPosition: 'center',
          width: '100%',
        }}
      />
    </Box>
  );
}

interface AttachmentPdfProps {
  sizeInPx: number;
}

function AttachmentPdf({ sizeInPx }: AttachmentPdfProps) {
  return (
    <Box
      data-test-id="file-thumbnail"
      sx={(theme) => ({
        alignItems: 'center',
        border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
        borderRadius: 1,
        display: 'flex',
        flex: 0,
        height: `${sizeInPx}px`,
        justifyContent: 'center',
        minWidth: `${sizeInPx}px`,
        overflow: 'hidden',
      })}
    >
      <PictureAsPdfOutlined
        sx={(theme) => ({
          color: theme.brandV2.colors.treadRed,
          fontSize: `${sizeInPx / 2}px`,
        })}
      />
    </Box>
  );
}
